import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _toLower from 'lodash/toLower';

/**
 * @param {import('./types').Option[]} options
 */
export function getOpenState(options = []) {
  if (!_isArray(options) || _isEmpty(options)) return {};

  return options.reduce((acc, option) => {
    const data = {};
    if (!_isEmpty(option.options)) {
      data[option.id] = true;
    }

    return { ...acc, ...data, ...getOpenState(option.options) };
  }, {});
}

/**
 * @param {import('./types').Option[]} options
 */
export function createValueMap(options = []) {
  if (!_isArray(options) || _isEmpty(options)) return {};

  return options.reduce((acc, option) => {
    const subOptionsValueMap = createValueMap(option.options);
    return { ...acc, [option.id]: false, ...subOptionsValueMap };
  }, {});
}

export function createNestedCheckboxSchema(
  options = [],
  {
    idKey = 'id',
    labelKey = 'label',
    optionsKey = 'options',
    skipOption = () => false,
    tooltipGenerator = () => '',
    checkIsDisabled = () => false,
    checkIsInterminate = () => false,
    optionDataGenerator = () => null
  } = {}
) {
  if (!_isArray(options)) return [];

  return options.reduce((acc, option) => {
    const skip = skipOption(option);
    if (skip) return acc;

    const id = _get(option, idKey);
    const label = _get(option, labelKey);
    const options = _get(option, optionsKey, []);

    acc.push({
      id,
      // value: id,
      label,
      disabled: checkIsDisabled(option),
      tooltipText: tooltipGenerator(option),
      indeterminate: checkIsInterminate(option),
      rawData: _omit(option, 'optionsKey'),
      query: createNestedCheckboxSearchQuery(option, { labelKey, optionsKey }),
      ...optionDataGenerator(option),
      options: _isEmpty(options)
        ? []
        : createNestedCheckboxSchema(options, {
            idKey,
            labelKey,
            optionsKey,
            skipOption,
            checkIsDisabled,
            tooltipGenerator,
            checkIsInterminate,
            optionDataGenerator
          })
    });
    return acc;
  }, []);
}

export function getNestedCheckboxValuesMap(
  options = [],
  { idKey = 'id', optionsKey = 'options', checkIfChecked = () => false } = {}
) {
  if (!_isArray(options) || _isEmpty(options)) return {};

  return options.reduce((acc, option) => {
    const id = _get(option, idKey);
    const subOptions = _get(option, optionsKey, []);
    return {
      ...acc,
      [id]: checkIfChecked(option),
      ...getNestedCheckboxValuesMap(subOptions, {
        idKey,
        optionsKey,
        checkIfChecked
      })
    };
  }, {});
}

function createNestedCheckboxSearchQuery(
  option = {},
  { labelKey = 'label', optionsKey = 'options' } = {}
) {
  const generateQuery = (prefix = '', option) => {
    const label = _toLower(_get(option, labelKey, ''));
    const subOptions = _get(option, optionsKey, []);

    return subOptions.reduce(
      (acc, subOption) => generateQuery(acc, subOption),
      prefix ? `${prefix}+${label}` : label
    );
  };

  return generateQuery('', option);
}
